import * as ko from "knockout";
import {
    PureComputed,
    Subscribable,
    Computed,
    ObservableArray,
    components,
    Observable,
} from "knockout";

import { DocumentsService } from "../../backend/v1/services.gen";
import { DocumentWidgetSeed } from "../../backend/v1/types.gen";
import { getTranslation } from "../../lib/localize";
import { session } from "../../lib/pyratSession";
import {
    baseUrl,
    getUrl,
    getFormData,
    getReadableByteSizeObject,
    ReadableByteSize,
} from "../../lib/utils";

import template from "./documents.html";

import "./documents.scss";

interface Subjects {
    [key: string]: number[];
}

type FileStates = "unsent" | "uploading" | "uploaded" | "aborted" | "error" | "removed";

interface DocumentFile {
    file: File;
    name: typeof File.name;
    isNewDocument: boolean;
    pendingAction: Observable<boolean>;
    documentId: Observable<number>;
    humanSize: ReadableByteSize;
    state: Observable<FileStates>;
    stateText: ko.Observable<string>;
    uploadProgress: ko.Observable<number>;
    uploadAbort?: () => void;
    canAbortUpload?: Subscribable<boolean>;
    canDeleteFile?: Subscribable<boolean>;
    downloadHref?: Subscribable<string>;
}

type reSeedCallback = () => void;

interface DocumentsParams {
    enableUploading: Observable<boolean>;
    enableDeleting: Observable<boolean>;
    multipleFiles: Observable<boolean>;
    hideExistingDocuments: Observable<boolean>;
    onSubjectsGetSeed: Observable<boolean>;
    seed: DocumentWidgetSeed;
    reSeed: reSeedCallback;
    resizeTrigger: Observable;
    existingDocumentIds: Observable<number[]>;
    newDocumentIds: Observable<number[]>;
    documentIds: Observable<number[]>;
    uploadsInProgress: Observable<number>;
    pendingAction: Observable<boolean>;
}


/**
 * ViewModel
 *
 * 'in' and 'out' are indicating the direction the passed observables are bound. 'out' ones can't be
 * changed from outside. see `../extensions/gluedObservable.ts`
 *
 * @param params.enableUploading
 *      in, default: true, enable or disable the possibility to upload new files
 * @param.enableDeleting
 *      in, default: true, enable or disable the possibility to delete documents
 * @param.multipleFiles
 *      in, default: true, upload multiple files vs. one file
 *      TODO: if disabled, it is still possible to upload a single file multiple times
 * @param.hideExistingDocuments
 *      in, default: false, if true, the widget does not show existing documents fur passed subjects - only newly uploaded ones
 * @param.seed
 *      in, the seed for te component (from lib.document.get_widget_seed),
 *      to upload documents that are not attached to anything, pass undefined or do not pass seed at all
 * @param.reSeed
 *      in, default: null, if a function is passed in, it will be called every time a document was successful added/deleted
 * @param.onSubjectsGetSeed
 *      in, default: true, when subjects changes, fetch new seed
 * @param.resizeTrigger
 *      out, default: <triggerOnly>, that observable will be triggered when a new files was added and
 *      the outer frame/window should update the height
 * @param.existingDocumentIds
 *      out, default: [], observable with a list of documentIds that where already assigned to subjects
 *      when the widget was loaded
 * @param.newDocumentIds
 *      out, default: [], observable with a list of documentIds that where newly uploaded by this widget
 * @param.documentIds
 *      out, default: [], observable with a list of documentIds of existing and new documentIds
 * @param.uploadsInProgress
 *      out, default: 0, int of how many uploads are currently in progress
 * @param.pendingAction
 *      out, default: true, indication if the widget is currently doing something (like uploading/deleting documents)
 *      this should be used to disable the outer form
 */
class DocumentsViewModel {
    private uploadFile = (fileData: DocumentFile) => {

        // File Upload is done via XMLHttpRequest (XHR), because it
        // allows showing the upload progress per file. In Chrome, we could also
        // use fetch with a ReadableStream, but in Firefox it is not supported yet.
        const xhr = new XMLHttpRequest();

        fileData.state("uploading");
        fileData.pendingAction(true);
        fileData.uploadAbort = (() => {
            xhr.abort();
        });

        const formData = getFormData({});
        formData.append("file", fileData.file);
        if (Object.keys(this.subjects()).length) {
            formData.append("subjects", JSON.stringify(this.subjects()));
        }

        // important: bind upload event listener before xhr.open call
        xhr.upload.addEventListener(
            "progress",
            (ev) => {
                if (ev.lengthComputable) {
                    fileData.uploadProgress(ev.loaded / ev.total || 0);
                }
            },
            false,
        );
        xhr.addEventListener(
            "loadend",
            () => {
                fileData.pendingAction(false);
                if (xhr.status === 0) {
                    fileData.state("aborted");
                    fileData.stateText(getTranslation("aborted"));
                } else if (xhr.status === 200) {
                    try {
                        const documentId: number = JSON.parse(xhr.responseText);
                        fileData.documentId(documentId);
                        fileData.state("uploaded");
                        fileData.stateText(getTranslation("OK"));
                    } catch (e) {
                        // parsing error
                        fileData.state("error");
                        fileData.stateText(getTranslation("Error while uploading document"));
                        return;
                    }
                    this.callReSeed();
                } else {
                    fileData.state("error");
                    fileData.stateText(getTranslation("Error while uploading document"));
                }
                this.queuedUpload();
            },
            false,
        );
        xhr.open("POST", baseUrl("backend/v1/documents"), true);
        xhr.setRequestHeader("Authorization", "Bearer " + session.sessionId);
        xhr.send(formData);
    };

    private queuedUpload = () => {
        let i;
        let fileData;
        for (i = 0; i < this.files().length && this.uploadsInProgress() < 3; i++) {
            fileData = this.files()[i];
            if (fileData.state() === "unsent") {
                this.uploadFile(fileData);
            }
        }
    };

    private onFileDataStateChange = (newValue: FileStates) => {
        if (["unsent", "uploading", "uploaded", "aborted", "error", "removed"].indexOf(newValue) === -1) {
            throw Error("unknown state");
        }
    };

    private getHrefComputed = (fileData: DocumentFile): Computed | null => {
        return ko.pureComputed(() => {
            if (fileData.documentId()) {
                return getUrl(baseUrl(`frontend/documents/${fileData.documentId()}`));
            }
            return null;
        });
    };

    private addFile = (file: File) => {
        const fileData: DocumentFile = {
            file: file,
            name: file.name,
            isNewDocument: true,
            pendingAction: ko.observable(true),
            documentId: ko.observable(null),
            humanSize: getReadableByteSizeObject(file.size),
            state: ko.observable("unsent"), // possible values: unsent, uploading, uploaded, aborted, error, removed
            stateText: ko.observable(""),
            uploadProgress: ko.observable(0),
            uploadAbort: null,
        };

        fileData.state.subscribe(this.onFileDataStateChange);

        fileData.canAbortUpload = ko.pureComputed(() => {
            return fileData.state() === "uploading";
        });

        fileData.canDeleteFile = ko.pureComputed(() => {
            return Boolean(this.enableDeleting()
                && fileData.state() === "uploaded"
                && fileData.documentId() !== null);
        });

        fileData.downloadHref = this.getHrefComputed(fileData);
        this.files.push(fileData);
        this.sortFiles();
        this.queuedUpload();
        this.resizeTrigger.valueHasMutated();
    };

    private digestSeed = () => {
        this.files([]);
        if (this.seed().subjects) {
            this.subjects(this.seed().subjects);
        }
        if (this.seed().documents) {
            this.seed().documents.forEach((entry) => {
                const fileData: DocumentFile = {
                    file: null,
                    name: entry.name,
                    isNewDocument: false,
                    pendingAction: ko.observable(false),
                    documentId: ko.observable(entry.id),
                    humanSize: getReadableByteSizeObject(entry.size),
                    state: ko.observable("uploaded"),
                    stateText: ko.observable(entry.locale_upload_datetime_str),
                    uploadProgress: ko.observable(0),
                    uploadAbort: null,
                    canAbortUpload: ko.observable(false),
                };

                fileData.canDeleteFile = ko.pureComputed(() => {
                    return Boolean(this.enableDeleting()
                        && fileData.state() === "uploaded"
                        && entry.can_delete
                        && fileData.documentId() !== null);
                });

                fileData.state.subscribe(this.onFileDataStateChange);
                fileData.downloadHref = this.getHrefComputed(fileData);
                this.files.push(fileData);
            });
            this.sortFiles();
            this.resizeTrigger.valueHasMutated();
        }
    };

    private handleEventFiles = (files: FileList) => {
        for (let i = 0; i < files.length; i++) {
            this.addFile(files[i]);
        }
    };

    private stopEvent = (ev: JQuery.TriggeredEvent | Event) => {
        ev.stopPropagation();
        ev.preventDefault();
        if ("originalEvent" in ev) {
            this.stopEvent(ev.originalEvent);
        }
    };

    private sortFiles = () => {
        this.files.sort((a, b) => {
            const lowerA = a.name.toLowerCase();
            const lowerB = b.name.toLowerCase();

            return lowerA < lowerB ? -1 :
                lowerA > lowerB ? 1 :
                0;
        });
    };

    private callReSeed = () => {
        if (typeof this.reSeed === "function") {
            this.reSeed();
        }
    };

    public subjects: Observable<Subjects>;
    public enableUploading: Observable<boolean>;
    public enableDeleting: Observable<boolean>;
    public multipleFiles: Observable<boolean>;
    public hideExistingDocuments: Observable<boolean>;
    public onSubjectsGetSeed: Observable<boolean>;
    public resizeTrigger: Observable<any>;
    public seed: Observable<DocumentWidgetSeed>;
    public reSeed: reSeedCallback;
    public files: ObservableArray<DocumentFile>;
    public filesToShow: PureComputed<DocumentFile[]>;
    public existingDocumentIds: PureComputed<number[]>;
    public newDocumentIds: PureComputed<number[]>;
    public documentIds: PureComputed<number[]>;
    public mouseOverDropZone: Observable<boolean>;
    public uploadsInProgress: PureComputed<number>;
    public pendingAction: PureComputed<boolean>;

    public onFileInputChange = (model: DocumentsViewModel, ev: JQuery.ChangeEvent) => {
        this.handleEventFiles(ev.target.files);
        // reset input element
        ev.target.value = "";
    };

    public onDragEnter = (model: DocumentsViewModel, ev: JQuery.DragEnterEvent) => {
        this.stopEvent(ev);
        this.mouseOverDropZone(true);
    };

    public onDragOver = (model: DocumentsViewModel, ev: JQuery.DragOverEvent) => {
        this.stopEvent(ev);
        this.mouseOverDropZone(true);
    };

    public onDragLeave = (model: DocumentsViewModel, ev: JQuery.DragLeaveEvent) => {
        this.stopEvent(ev);
        this.mouseOverDropZone(false);
    };

    public onDrop = (model: DocumentFile, ev: JQuery.DropEvent) => {
        this.stopEvent(ev);
        this.mouseOverDropZone(false);
        this.handleEventFiles(ev.originalEvent.dataTransfer.files);
    };

    public onRemove = (fileData: DocumentFile) => {
        if (fileData.canAbortUpload() && (typeof (fileData.uploadAbort) === "function")) {
            fileData.uploadAbort();
        } else if (fileData.canDeleteFile()) {
            // remember and unset documentId to prevent double clicking on delete button
            const documentId = fileData.documentId();
            fileData.pendingAction(true);
            fileData.documentId(null);
            DocumentsService.removeDocumentSubjects({
                documentId,
                requestBody: { subjects: this.subjects() },
            }).catch(() => {
                fileData.state("error");
                fileData.stateText(getTranslation("Error while deleting document"));
            }).finally(() => {
                fileData.pendingAction(false);
                fileData.state("removed");
                fileData.stateText(getTranslation("removed"));
            });
        }
    };

    constructor(params: DocumentsParams) {

        this.subjects = ko.observable({});
        this.enableUploading = ko.utils.gluedObservable(params.enableUploading, true, "in");
        this.enableDeleting = ko.utils.gluedObservable(params.enableDeleting, true, "in");
        this.multipleFiles = ko.utils.gluedObservable(params.multipleFiles, true, "in");
        this.hideExistingDocuments = ko.utils.gluedObservable(params.hideExistingDocuments, false, "in");
        this.onSubjectsGetSeed = ko.utils.gluedObservable(params.onSubjectsGetSeed, true, "in");
        this.seed = ko.utils.gluedObservable(params.seed, null, "in");
        this.seed.subscribe(this.digestSeed);
        this.resizeTrigger = ko.utils.gluedObservable(params.resizeTrigger, null, "out", true);
        this.reSeed = params.reSeed;
        this.files = ko.observableArray();
        this.filesToShow = ko.pureComputed(() => {
            return this.files().filter((fileData) => {
                return this.hideExistingDocuments() === false || fileData.isNewDocument === true;
            });
        }).extend({ deferred: true });
        this.existingDocumentIds = ko.utils.gluedObservable(params.existingDocumentIds, [], "out", false,
            ko.pureComputed(() => {
                return this.files().reduce((acc, fileData) => {
                    if (fileData.isNewDocument === false && fileData.documentId()) acc.push(fileData.documentId());
                    return acc;
                }, []);
            }),
        );
        this.newDocumentIds = ko.utils.gluedObservable(params.newDocumentIds, [], "out", false,
            ko.pureComputed(() => {
                return this.files().reduce((acc, fileData) => {
                    if (fileData.isNewDocument === true && fileData.documentId()) acc.push(fileData.documentId());
                    return acc;
                }, []);
            }),
        );
        this.documentIds = ko.utils.gluedObservable(params.documentIds, undefined, "out", false,
            ko.pureComputed(() => {
                return this.existingDocumentIds().concat(this.newDocumentIds());
            }),
        );
        this.mouseOverDropZone = ko.observable(false);
        this.uploadsInProgress = ko.utils.gluedObservable(params.uploadsInProgress, 0, "out", false,
            ko.pureComputed(() => {
                return this.files().reduce((acc, file) => {
                    if (file.state() === "uploading") {
                        acc++;
                    }
                    return acc;
                }, 0);
            }),
        );
        this.pendingAction = ko.utils.gluedObservable(params.pendingAction, true, "out", false,
            ko.pureComputed(() => {
                return this.files().some(fileData => fileData.pendingAction() === true);
            }),
        );

        // init
        if (this.seed()) {
            this.digestSeed();
        }
    }
}


export class DocumentsComponent {

    constructor() {

        return {
            viewModel: {
                createViewModel: (params: DocumentsParams, componentInfo: components.ComponentInfo) => {

                    // ensure the component is used through <ko-documents/> HTML element so we can use scoped styles
                    if (componentInfo.element.nodeName.toLowerCase() !== "ko-documents") {
                        throw new Error("Only use the documents component through <ko-documents/> HTML tag.");
                    }

                    return new DocumentsViewModel(params);
                },
            },
            template,
        };
    }
}
